import { create } from 'zustand'
import { MenuItem } from '@src/api/common/menu'
import { IconMap } from '@src/utils/common/iconMap'
import { fetcher } from '@src/utils/fetcher'
import React from 'react'

/**
 * 是否系统管理员 —— 用于决定是否注入"系统配置"菜单
 * 后端 sys_menu 表同步上线后,这条规则改由后端控制(菜单记录本身带权限),
 * 前端这个 fallback 兜底仅用于开发期
 *
 * ⚠️ 演示期临时放开:先 return true,所有登录用户都能看到"系统配置"
 * 正式上线前恢复 role 判断
 */
function isSystemAdmin(): boolean {
	return true
}

// 后端返回的菜单项类型（可选，用于类型安全）
interface ApiMenu {
	id: number
	parentMenuId: number
	textKey: string
	iconName: string | null
	menuPath: string | null
	orderNum: number
	hideMenu: boolean
	children: ApiMenu[] | null
}

// 已知菜单（后端 iconName 可能为空）的前端兜底图标，按 i18n key 注册
const MENU_DEFAULT_ICONS: Record<string, React.ComponentType> = {
	'app.menu.crm': IconMap.IconUser,
	'app.menu.wecom': IconMap.IconNotification
}

interface MenuState {
	menuList: MenuItem[]
	loading: boolean
	loaded: boolean
	fetchMenuList: () => Promise<void>
}

// ⚠️ Phase 1 临时兜底：CRM 线索模块尚未写入 sys_menu 表，前端注入 fallback
// 后端补齐菜单记录后可移除本块。
// 线索模板 编辑版 移出 CRM(放到下面 FALLBACK_SYSTEM_CONFIG_MENU)
// 留一份"字段展示版"(只读)给所有用户看 schema,菜单标签也是"线索模板"
const FALLBACK_CRM_MENU: MenuItem = {
	itemKey: 'fallback-crm',
	text: 'app.menu.crm',
	hide: false,
	path: undefined,
	icon: IconMap.IconUser,
	items: [
		{
			// 字段展示版(只读 schema 浏览器),与系统配置下的编辑版同名但入口不同
			itemKey: 'fallback-crm-fields',
			text: 'app.menu.crm.lead.fields',
			hide: false,
			path: '/lead/fields',
			icon: IconMap.IconForm
		},
		{
			itemKey: 'fallback-crm-source',
			text: 'app.menu.crm.lead.source',
			hide: false,
			path: '/lead/source',
			icon: IconMap.IconLink
		},
		{
			itemKey: 'fallback-crm-rule',
			text: 'app.menu.crm.lead.rule',
			hide: false,
			path: '/lead/rule',
			icon: IconMap.IconSetting
		},
		{
			itemKey: 'fallback-crm-pool',
			text: 'app.menu.crm.lead.pool',
			hide: false,
			path: '/lead/pool',
			icon: IconMap.IconUsers
		},
		{
			itemKey: 'fallback-crm-list',
			text: 'app.menu.crm.lead.list',
			hide: false,
			path: '/lead/list',
			icon: IconMap.IconList
		}
	]
}

// ⚠️ Phase 1 临时兜底：系统配置菜单(线索模板等),仅对系统管理员注入
// 后端 sys_menu 上线后由后端按角色控制可见性,本 fallback 仅做开发期兜底
const FALLBACK_SYSTEM_CONFIG_MENU: MenuItem = {
	itemKey: 'fallback-system-config',
	text: 'app.menu.systemConfig',
	hide: false,
	path: undefined,
	icon: IconMap.IconSetting,
	items: [
		{
			itemKey: 'fallback-system-config-lead-template',
			text: 'app.menu.systemConfig.leadTemplate',
			hide: false,
			path: '/lead/template',
			icon: IconMap.IconForm
		}
	]
}

// ⚠️ Phase 1 临时兜底：H5 移动端预览模块（H5 预览是 top-level,跟进计划是 sub）
const FALLBACK_H5_PREVIEW_MENU: MenuItem = {
	itemKey: 'fallback-h5-preview',
	text: 'app.menu.h5Preview',
	hide: false,
	path: undefined,
	icon: IconMap.IconPhone,
	items: [
		{
			itemKey: 'fallback-h5-preview-follow-plan',
			text: 'app.menu.followPlan',
			hide: false,
			path: '/follow-plan/calendar',
			icon: IconMap.IconList
		}
	]
}

// ⚠️ Phase 1 临时兜底：企微管理模块尚未写入 sys_menu 表，前端注入 fallback
// 后端补齐菜单记录后可移除本块。
const FALLBACK_WECOM_MENU: MenuItem = {
	itemKey: 'fallback-wecom',
	text: 'app.menu.wecom',
	hide: false,
	path: undefined,
	icon: IconMap.IconNotification,
	items: [
		{
			itemKey: 'fallback-wecom-active-code',
			text: 'app.menu.wecom.activeCode',
			hide: false,
			path: '/wecom/active-code',
			icon: IconMap.IconLink
		},
		{
			itemKey: 'fallback-wecom-colleague',
			text: 'app.menu.wecom.colleague',
			hide: false,
			path: '/wecom/colleague',
			icon: IconMap.IconUser
		},
		{
			itemKey: 'fallback-wecom-group',
			text: 'app.menu.wecom.group',
			hide: false,
			path: '/wecom/group',
			icon: IconMap.IconUserGroup
		},
		{
			itemKey: 'fallback-wecom-customer',
			text: 'app.menu.wecom.customer',
			hide: false,
			path: '/wecom/customer',
			icon: IconMap.IconTag
		}
	]
}

const useMenuStore = create<MenuState>((set, get) => ({
	menuList: [],
	loading: false,
	loaded: false,

	fetchMenuList: async () => {
		set({ loading: true })
		try {
			const res = await fetcher<{ data: ApiMenu[] }>('/sys/usermenu', 'GET')
			let converted = convertApiMenuToMenuItem(res.data || [])
			// 给已知菜单补前端兜底 icon(后端 iconName 为空时仍可见图标)
			converted = applyDefaultIcons(converted)
			// 系统配置菜单(线索模板等)仅对系统管理员开放 —— 前端兜底,后端 sys_menu 上线后由后端控制
			if (!isSystemAdmin()) {
				converted = converted.filter((m) => m.text !== 'app.menu.systemConfig')
			}
			// 兜底：若后端没返回 CRM / 企微管理菜单，则注入 fallback（Phase 1 临时）
			const hasCrm = converted.some(
				(m) => m.text === 'app.menu.crm' || (m.items || []).some((c) => c.text?.startsWith('app.menu.crm'))
			)
			const hasWecom = converted.some(
				(m) => m.text === 'app.menu.wecom' || (m.items || []).some((c) => c.text?.startsWith('app.menu.wecom'))
			)
			const hasH5Preview = converted.some(
				(m) => m.text === 'app.menu.h5Preview' || (m.items || []).some((c) => c.text === 'app.menu.followPlan')
			)
			const hasSystemConfig = converted.some(
				(m) => m.text === 'app.menu.systemConfig' || (m.items || []).some((c) => c.text?.startsWith('app.menu.systemConfig'))
			)
			const fallbacks: MenuItem[] = []
			if (!hasCrm) fallbacks.push(FALLBACK_CRM_MENU)
			if (!hasWecom) fallbacks.push(FALLBACK_WECOM_MENU)
			if (!hasH5Preview) fallbacks.push(FALLBACK_H5_PREVIEW_MENU)
			// 系统配置 fallback 仅在 admin 时注入
			if (!hasSystemConfig && isSystemAdmin()) fallbacks.push(FALLBACK_SYSTEM_CONFIG_MENU)
			const final = fallbacks.length ? [...converted, ...fallbacks] : converted
			set({ menuList: final, loaded: true })
		} catch (err) {
			// 接口失败时使用纯 fallback,让用户至少能看到 CRM 与企微管理模块
			const fallbacks: MenuItem[] = [FALLBACK_CRM_MENU, FALLBACK_WECOM_MENU, FALLBACK_H5_PREVIEW_MENU]
			if (isSystemAdmin()) fallbacks.push(FALLBACK_SYSTEM_CONFIG_MENU)
			set({ menuList: fallbacks, loaded: true })
		} finally {
			set({ loading: false })
		}
	}
}))

/**
 * 将 API 返回的菜单树转换为前端 MenuItem 格式
 */
export function convertApiMenuToMenuItem(apiMenus: ApiMenu[]): MenuItem[] {
	return apiMenus
		.map((item) => {
			const converted: MenuItem = {
				itemKey: String(item.id), // 转为字符串，匹配原有 '1', '1-1' 风格
				text: item.textKey, // i18n key，如 'app.menu.dashboard'
				hide: item.hideMenu,
				path: item.menuPath || undefined, // null 或空字符串转为 undefined
				icon: item.iconName ? IconMap[item.iconName] : undefined,
				items: item.children ? convertApiMenuToMenuItem(item.children) : undefined
			}
			return converted
		})
		.sort((a, b) => {
			// 可选：按 orderNum 排序（如果后端未排序）
			// 但你的数据已按 orderNum 排好，可省略
			return 0
		})
}

/**
 * 对已知菜单（后端 iconName 可能为空）补一个前端兜底图标。
 * 只补缺；不覆盖后端给的值。
 */
function applyDefaultIcons(items: MenuItem[]): MenuItem[] {
	return items.map((item) => ({
		...item,
		icon: item.icon || MENU_DEFAULT_ICONS[item.text],
		items: item.items ? applyDefaultIcons(item.items) : item.items
	}))
}

export default useMenuStore
